import store2 from 'store2'
import { LitElement, html, property } from 'lit-element'
import { contextualize, cutlassEvent } from '@polleverywhere/cutlass'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { register } from '@polleverywhere/hmr-custom-element'
import { peFetch, apiUrlFor } from '@polleverywhere/fetch'

import '@polleverywhere/pe-button'
import '@polleverywhere/pe-modal'
import '@polleverywhere/pe-text-field'

import './pe-nps-feedback.scss'

export const STORAGE_KEY = 'nps-feedback'

function validate () {
  const data = new FormData(this.querySelector('form'))
  this.querySelector('pe-button[submit]').disabled = !data.get('nps-feedback-score')
}

function handleSubmit (e) {
  e.preventDefault()
  e.stopPropagation()

  const data = new FormData(this.querySelector('form'))
  cutlassEvent(this, `${this.usrc ? this.usrc : 'unknown'}:nps-response`, {
    score: data.get('nps-feedback-score'),
    reason: data.get('nps-feedback-reason')
  })
  this.close('feedback')

  this.dispatchEvent(new CustomEvent('success'))
}

function handleDismiss (e) {
  cutlassEvent(this, 'later')
  this.close('later')
}

function modalTemplate ({ isModal, template } = {}) {
  return isModal ? html`<pe-modal open>${template()}</pe-modal>` : template()
}

function template () {
  return html`
    <div class=${bem('wrapper')}>
      <div class=${bem('content')}>
        <h2>${this.title}</h2>
        <form @submit=${handleSubmit} @change=${validate}>
          <fieldset>
            <legend>On a scale of 0-10, how likely is it that you would recommend <br> Poll Everywhere to a friend or colleague?</legend>
            <div class=${bem('score')}>
            ${[...Array(11)].map((_, i) => html`
              <input type=radio id=${`nps-feedback-score-${i}`} name=nps-feedback-score .value=${i.toString()} />
              <label for=${`nps-feedback-score-${i}`}>${i}</label>
            `)}
          </div>
          </fieldset>
          <p>What is the most important reason for your score?</p>
          <pe-text-field name=nps-feedback-reason placeholder="Optional reason" maxLength=300 char-counter></pe-text-field>
          <div class=${bem('actions')}>
            <pe-button label="Ask me later" type=link @click=${handleDismiss}></pe-button>
            <pe-button label="Send feedback" submit disabled></pe-button>
          </div>
        </form>
      </div>
    </div>
  `
}

/**
 * Collect nps scores from users using cutlass to track the results.
 *
 * This component also provides several utilities to help build automated
 * feedback collection:
 *  import { countVisit, visitCount, weeksSince, completedOrDelayed } from '@polleverywhere/pe-nps-feedback/utils'
 *
 *  countVisit() - persist a count in localstorage to track number of visits to
 *                 the nps widget
 *  visitCount() - returns the number of visits tracked by visitCount
 *  weeksSince() - calc the weeks since a data
 *  completedOrDelayed() - checks to see what the last tracked nps action was
 *                         and if it is a good time to show the feedback
 *                         if it was the feedback action then consider hiding feedback
 *                         if it was the later action and a week later then consider
 *                         showing the nps
 *
 * Event names will take the form: "<user_source>:nps-response"
 *
 * When you want to use this component, bring it in to your context with
 * `import @polleverywhere/pe-nps-feedback`
 *
 * @element pe-nps-feedback
 *
 * @prop {boolean} modal=false  -  Show as a modal or full page feedback widget
 * @prop {string}  usrc         -  The source of the feedback
 * @prop {string}  title        -  Title of the feedback widget
 * @prop {number}  userId       -  The users submitting the feedback
 *
 * @fires {Object} success      -  Fires when the feedback was sent
 * @fires {Object} close        -  Fires when the user as requested to
 *                                 close the modal or cancel feedback
 */
export default class PeNpsFeedback extends LitElement {
  static get tagName () { return 'pe-nps-feedback' }

  @property({ type: Number, attribute: 'user-id' })
  userId

  @property({ type: String, reflect: true })
  usrc

  @property({ type: String, reflect: true })
  title='Help us improve Poll Everywhere'

  @property({ type: Boolean, reflect: true })
  modal = false

  createRenderRoot () { return this }

  constructor () {
    super()
    contextualize(this, this.tagName)
  }

  firstUpdated () {
    cutlassEvent(this, 'show')
  }

  render () {
    return modalTemplate({ isModal: this.modal, template: template.bind(this) })
  }

  close (action = 'later') {
    this.remove()
    store2.set(STORAGE_KEY, {
      action,
      when: new Date()
    })

    if (this.userId) {
      peFetch(apiUrlFor(`/api/users/${this.userId}/prompt`), {
        method: 'PUT',
        body: {
          prompt: 'nps_feedback',
          response: action === 'feedback' ? 'complete' : 'later'
        }
      })
    }
    this.dispatchEvent(new CustomEvent('close'))
  }
}

const bem = bemBlock(PeNpsFeedback.tagName)
register(module, PeNpsFeedback.tagName, PeNpsFeedback)
